import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import CircularProgress from "@material-ui/core/CircularProgress";
import localizationManager from "./localizationManager";
import SEOHead from "../components/SEOHead";
import { getLangPrefix } from "../utils/langUrl";
import marked from "marked";

const styles = (theme) => ({
  root: {
    padding: theme.spacing.unit * 4,
    maxWidth: 1200,
    margin: "0 auto",
    marginTop: 80, // for AppBar
    minHeight: "70vh",
  },
  articleContainer: {
    padding: theme.spacing.unit * 4,
    "& h1": {
      ...theme.typography.h3,
      marginBottom: theme.spacing.unit * 3,
      fontWeight: 700,
    },
    "& h2": {
      ...theme.typography.h4,
      marginTop: theme.spacing.unit * 4,
      marginBottom: theme.spacing.unit * 2,
      fontWeight: 600,
      color: theme.palette.secondary.main,
    },
    "& h3": {
      ...theme.typography.h5,
      marginTop: theme.spacing.unit * 3,
      marginBottom: theme.spacing.unit * 2,
      fontWeight: 600,
    },
    "& p": {
      ...theme.typography.body1,
      fontSize: "1.05rem",
      lineHeight: 1.7,
      marginBottom: theme.spacing.unit * 2,
    },
    "& ul, & ol": {
      ...theme.typography.body1,
      fontSize: "1.05rem",
      lineHeight: 1.7,
      marginBottom: theme.spacing.unit * 3,
      paddingLeft: theme.spacing.unit * 4,
    },
    "& li": {
      marginBottom: theme.spacing.unit,
    },
    "& table": {
      width: "100%",
      borderCollapse: "collapse",
      marginBottom: theme.spacing.unit * 4,
    },
    "& th, & td": {
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing.unit * 1.5,
    },
    "& th": {
      backgroundColor: theme.palette.grey[100],
      fontWeight: 600,
      textAlign: "left",
    },
    "& img": {
      maxWidth: "100%",
      height: "auto",
      borderRadius: 4,
      marginBottom: theme.spacing.unit * 2,
    },
    "& a": {
      color: theme.palette.secondary.main,
      textDecoration: "none",
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },
  mainImage: {
    width: "100%",
  height: "auto",
  borderRadius: 8,
  marginBottom: theme.spacing.unit * 4,
  display: "block",
  },
  sidebar: {
    padding: theme.spacing.unit * 3,
    backgroundColor: theme.palette.grey[50],
  },
  sidebarTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing.unit * 2,
    borderBottom: `2px solid ${theme.palette.secondary.main}`,
    paddingBottom: theme.spacing.unit,
    display: "inline-block",
  },
  relatedLink: {
    display: "block",
    color: theme.palette.text.primary,
    textDecoration: "none",
    padding: `${theme.spacing.unit}px 0`,
    transition: "color 0.2s",
    "&:hover": {
      color: theme.palette.secondary.main,
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "50vh",
  },
  breadcrumb: {
    marginBottom: theme.spacing.unit * 3,
    "& a": {
      color: theme.palette.text.secondary,
      textDecoration: "none",
      "&:hover": {
        color: theme.palette.secondary.main,
      },
    },
  },
});

class ArticleDetail extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      articleMeta: null,
      contentHTML: "",
      groupedArticles: {}, // category -> list of {slug, title}
      loading: true,
      error: false,
    };
  }

  componentDidMount() {
    this.loadArticle();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.match.params.slug !== this.props.match.params.slug ||
      prevProps.match.params.lang !== this.props.match.params.lang
    ) {
      this.loadArticle();
    }
  }

  loadArticle = async () => {
    this.setState({ loading: true, error: false });
    const { slug } = this.props.match.params;
    let lang = localizationManager.currentLang;
    if (lang === "zh" || lang === "zh-tw") {
      lang = "zh-TW";
    }

    try {
      // 1. Fetch metadata
      const resMeta = await fetch("/data/articles.json");
      if (!resMeta.ok) throw new Error("Could not fetch articles index");
      const allArticles = await resMeta.json();
      
      const articleMeta = allArticles.find((a) => a.slug === slug);
      if (!articleMeta) {
        throw new Error("Article not found");
      }

      // Group all articles by category
      const groupedArticles = allArticles.reduce((acc, a) => {
        const cat = a.category || "General";
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push({
          slug: a.slug,
          title: (a.locales[lang] || a.locales["en"]).title,
        });
        return acc;
      }, {});

      const localeData = articleMeta.locales[lang] || articleMeta.locales["en"];
      const mdUrl = localeData.file;

      // 2. Fetch Markdown content
      const resMd = await fetch(mdUrl);
      if (!resMd.ok) throw new Error("Could not fetch markdown content");
      const mdText = await resMd.text();

      // Convert Markdown to HTML with language-aware links
      const renderer = new marked.Renderer();
      const langPrefix = getLangPrefix(lang);
      renderer.link = function (href, title, text) {
        // Prepend language prefix to internal links
        if (href && href.startsWith("/") && !href.startsWith("//")) {
          href = langPrefix + href;
        }
        const titleAttr = title ? ` title="${title}"` : "";
        return `<a href="${href}"${titleAttr}>${text}</a>`;
      };
      const contentHTML = marked(mdText, { renderer });

      this.setState({
        articleMeta,
        contentHTML,
        groupedArticles,
        loading: false,
      });
    } catch (err) {
      console.error(err);
      this.setState({ loading: false, error: true });
    }
  };

  createMarkup() {
    return { __html: this.state.contentHTML };
  }

  render() {
    const { classes } = this.props;
    const { articleMeta, groupedArticles, loading, error } = this.state;
    
    let lang = localizationManager.currentLang;
    if (lang === "zh" || lang === "zh-tw") {
      lang = "zh-TW";
    }
    const langPrefix = getLangPrefix(lang);

    if (loading) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress color="secondary" />
        </div>
      );
    }

    if (error || !articleMeta) {
      return (
        <div className={classes.root}>
          <Typography variant="h5" color="error" align="center">
            {localizationManager.getLocalString("@@error_loading_article") || "Article not found or failed to load."}
          </Typography>
        </div>
      );
    }

    const localeData = articleMeta.locales[lang] || articleMeta.locales["en"];

    return (
      <div className={classes.root}>
        <SEOHead
          title={localeData.title + " | " + localizationManager.getLocalStringPlain("@@linkchamp")}
          description={localeData.excerpt}
          location={this.props.location}
          lang={localizationManager.currentLang}
        />

        <Typography className={classes.breadcrumb} variant="body2">
          <Link to={`${langPrefix}/news`}>
            {localizationManager.getLocalString("@@news")}
          </Link>
          {" > "}
          <Link to={`${langPrefix}/articles`}>
            {localizationManager.getLocalString("@@knowledge_hub")}
          </Link>
          {" > "}
          <span style={{ color: "#000" }}>{localeData.title}</span>
        </Typography>

        <Grid container spacing={32}>
          {/* Main Content */}
          <Grid item xs={12} md={8} lg={9}>
            <Paper className={classes.articleContainer} elevation={1}>
               <Typography variant="h3" component="h1" gutterBottom style={{fontWeight: 'bold'}}>
                 {localeData.title}
               </Typography>
               <img
                 src={articleMeta.image}
                 className={classes.mainImage}
                 alt={localeData.title}
               />
               <div dangerouslySetInnerHTML={this.createMarkup()} />
            </Paper>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4} lg={3}>
            <Paper className={classes.sidebar} elevation={0}>
              <Typography variant="h6" className={classes.sidebarTitle}>
                {localizationManager.getLocalString("@@knowledge_hub") || "Knowledge Hub"}
              </Typography>
              
              {Object.keys(groupedArticles).map((category) => (
                <div key={category} style={{ marginBottom: 24 }}>
                  <Typography 
                    variant="subtitle2" 
                    color="secondary" 
                    style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '1px', marginBottom: 8 }}
                  >
                    {category}
                  </Typography>
                  {groupedArticles[category].map((rel) => (
                    <Link
                      key={rel.slug}
                      to={`${langPrefix}/articles/${rel.slug}`}
                      className={classes.relatedLink}
                      style={{ 
                        fontWeight: rel.slug === articleMeta.slug ? 700 : 400,
                        color: rel.slug === articleMeta.slug ? '#000' : 'inherit',
                        borderLeft: rel.slug === articleMeta.slug ? '2px solid' : 'none',
                        paddingLeft: rel.slug === articleMeta.slug ? 8 : 0,
                        marginLeft: rel.slug === articleMeta.slug ? -10 : 0,
                      }}
                      onClick={() => window.scrollTo(0,0)}
                    >
                      {rel.title}
                    </Link>
                  ))}
                </div>
              ))}
            </Paper>
          </Grid>
        </Grid>
      </div>
    );
  }
}

ArticleDetail.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

export default withStyles(styles)(ArticleDetail);
